import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { token as liveToken } from '../auth/session';
import WhereButton from '../ai/WhereButton';
import { translate, useT } from '../i18n/i18n';

export type HelperWord = { id: string; word: string; type: string; hidden: boolean; own: boolean };
export type HelperPage = { id: string; label: string; words: HelperWord[] };
export type HelperChange = { op: 'add' | 'hide' | 'show' | 'remove'; page: string | null; id: string | null; word: string | null; picture: string | null; type: string | null };

const OP_TEXT: Record<HelperChange['op'], string> = { add: 'Add', hide: 'Hide', show: 'Show', remove: 'Remove' };

export default function TalkHelper({ pages, core, onApply }: {
  pages: HelperPage[];
  core: HelperWord[];
  onApply: (changes: HelperChange[]) => void;
}) {
  const t = useT();
  const [command, setCommand] = useState('');
  const [busy, setBusy] = useState<'' | 'listening' | 'hearing' | 'thinking'>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ reply: string; changes: HelperChange[]; sent: string; requestId: string } | null>(null);
  const [speechId, setSpeechId] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  useEffect(() => () => { recorder.current?.stream.getTracks().forEach((t) => t.stop()); }, []);

  async function ask(text: string) {
    if (!text.trim()) return;
    setBusy('thinking');
    setError(null);
    setResult(null);
    try {
      setResult(await api('/ai/talk-board', { method: 'POST', token: true, body: { command: text.trim(), pages, core } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('No answer came back.'));
    } finally {
      setBusy('');
    }
  }

  async function listen() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        void hear(new Blob(chunks.current, { type: r.mimeType || 'audio/webm' }));
      };
      recorder.current = r;
      r.start();
      setBusy('listening');
    } catch {
      setError(t('The microphone could not be used. Allow it in the browser, or type instead.'));
    }
  }

  async function hear(audio: Blob) {
    setBusy('hearing');
    try {
      const t = await liveToken();
      const res = await fetch('/api/ai/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': audio.type.split(';')[0] || 'audio/webm', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: audio,
      });
      if (!res.ok) throw new Error(translate('What you said could not be turned into text. Try again or type it.'));
      const { text, requestId } = (await res.json()) as { text: string; requestId: string };
      setSpeechId(requestId);
      setCommand(text);
      if (text.trim()) await ask(text);
      else { setError(translate('Nothing was heard. Try again a little closer to the microphone.')); setBusy(''); }
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('What you said could not be turned into text.'));
      setBusy('');
    }
  }

  return (
    <div className="card tb-helper">
      <h3>{t('Ask EasyLearning AI to change the board')}</h3>
      <p className="field-hint">{t('Say or type what you want, for example "add pizza and chips to Food" or "hide tickle". You see the changes before anything is saved.')}</p>
      <div className="tb-helper-row">
        <input aria-label={t('What to change')} value={command} maxLength={500} placeholder={t('What to change')}
          onChange={(e) => setCommand(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setSpeechId(null); void ask(command); } }} />
        {busy === 'listening'
          ? <button type="button" className="btn" onClick={() => recorder.current?.stop()}>{t('Stop')}</button>
          : <button type="button" className="btn secondary" disabled={!!busy} onClick={() => void listen()}><span aria-hidden="true">🎤</span> {t('Speak')}</button>}
        <button type="button" className="btn" disabled={!!busy || !command.trim()} onClick={() => { setSpeechId(null); void ask(command); }}>{t('Ask')}</button>
      </div>
      {busy && <p role="status" className="muted">{busy === 'listening' ? t('Listening. Press Stop when you have finished.') : busy === 'hearing' ? t('Turning speech into text') : t('Working out the changes')}</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {result && (
        <div className="tb-helper-result">
          {result.reply && <p>{result.reply}</p>}
          {result.changes.length > 0 && (
            <>
              <ul>
                {result.changes.map((c, i) => (
                  <li key={i}>
                    {t(OP_TEXT[c.op])} {c.picture ? `${c.picture} ` : ''}<strong>{c.word}</strong>
                    {c.op === 'add' && c.page ? ` ${t('to {page}', { page: pages.find((p) => p.id === c.page)?.label ?? c.page })}` : ''}
                  </li>
                ))}
              </ul>
              <div className="toolbar">
                <button type="button" className="btn" onClick={() => { onApply(result.changes); setResult(null); setCommand(''); }}>{t('Make these changes')}</button>
                <button type="button" className="btn secondary" onClick={() => setResult(null)}>{t('Cancel')}</button>
              </div>
            </>
          )}
          <details className="ai-sent"><summary>{t('What was sent')}</summary><p>{result.sent}</p></details>
          <WhereButton ids={[speechId, result.requestId]} />
        </div>
      )}
    </div>
  );
}
